import { Theme, Breakpoint } from '@mui/material'

const IMAGE_EXT = '.jpg'

/**
 * Default Amplience Dynamic media API parameters:
 * https://amplience.com/docs/dynamicmedia/dmapireference.html
 */
const defaultParams: ImageParams = {
  fmt: 'auto',
  qlt: 'default',
  'fmt.jp2.qlt': 60,
  bg: 'rgb(255, 255, 255)',
  // 'fmt.jpeg.interlaced': true,
}

type ImageParams = { [key: string]: string | number | undefined | boolean }

/**
 * Converts all image urls to jpg
 * Adds/replaces params in the query string and appends templates
 */
export const processImageUrl = (
  urlString: string,
  params?: ImageParams,
  templates?: string[],
  isConfigProgressImage?: boolean
) => {
  try {
    const url = new URL(urlString)
    // Only apply default params if not set on the URL already
    Object.keys(defaultParams).forEach(
      (key) => !url.searchParams.has(key) && url.searchParams.set(key, '' + defaultParams[key])
    )
    // Apply all params passed in
    params && Object.keys(params).forEach((key) => url.searchParams.set(key, '' + params[key]))
    if (isConfigProgressImage) {
      url.searchParams.set('fmt.jpeg.interlaced', 'true')
    }
    const paramString = url.searchParams.toString()
    const templateString = templates?.join('&') || ''
    const query =
      templateString || paramString
        ? '?' + templateString + (templateString && paramString ? '&' : '') + paramString
        : ''

    const pathname = url.pathname.split('.')[0]
    return url.origin + pathname + IMAGE_EXT + query
  } catch (e) {
    // Invalid URL format
    return ''
  }
}

export const checkImageExists = async (imgUrl: string) => {
  return new Promise((resolve, reject) => {
    const loadImg = new Image()
    loadImg.src = imgUrl
    loadImg.onload = () =>
      setTimeout(() => {
        resolve(imgUrl)
      }, 50)
    loadImg.onerror = () => reject(new Error(`Image failed to load: ${imgUrl}`))
  })
}

export const getBreakpoints = (squareImage: boolean | undefined, fullWidthImage: boolean | undefined, theme: Theme) => {
  const breakpoints: {
    breakpoint: Breakpoint
    query: 'min-width' | 'max-width'
    w: number
    aspect: string
    h?: number
  }[] = [
    { breakpoint: 'xs', query: 'max-width', w: theme.breakpoints.values.xs, aspect: '1:1' },
    {
      breakpoint: 'sm',
      query: 'max-width',
      w: theme.breakpoints.values.sm,
      aspect: squareImage ? '1:1' : '2:1',
    },
    {
      breakpoint: 'md',
      query: 'max-width',
      w: fullWidthImage ? theme.breakpoints.values.md : theme.breakpoints.values.md / 2,
      aspect: fullWidthImage ? '2:1' : '1:1',
    },
    {
      breakpoint: 'lg',
      query: 'max-width',
      w: fullWidthImage ? theme.breakpoints.values.lg : theme.breakpoints.values.lg / 2,
      aspect: fullWidthImage ? '2:1' : '1:1',
    },
  ]

  return breakpoints
}

export const getAspectRatio = (squareImage: boolean) => {
  if (squareImage) {
    return '1:1'
  }
  return '2:1'
}

export const getDarknessAttribute = (darknessLevel?: number): { cm?: string; bg?: string } => {
  return typeof darknessLevel === 'number'
    ? { cm: 'dark', bg: `rgba(0,0,0, ${Math.round((100 - darknessLevel) * 2.55)})` }
    : {}
}
